/**
 * What the war did to the pilot this time.
 *
 * The manual gives the shape of this exactly: *"If you crash, you may either
 * walk away with no injuries, end up in a hospital, or be killed, depending
 * upon the severity of the crash. If you are hospitalized, you will remain
 * there until you recover from your injuries. If you land or crash (and
 * survive) in enemy territory, you may end up in an enemy prisoner-of-war
 * camp"*, and the cost: *"You can lose up to six months if you are hospitalized
 * or jailed"* (manual, "Fly Next Mission"). Wikipedia adds the escape — a
 * wounded pilot down in enemy territory "spend[s] several months in a
 * prisoner-of-war camp until they escape back to their side"
 * (docs/game/aces-squadrons.md §5) — which is what resolves the `xjail-s` /
 * `xhosp-s` asset pair `PS.EXE` carries.
 *
 * **The probabilities are port-side** and are the one thing here that no source
 * supplies; docs/game/campaign.md lists wound-severity grading as an open
 * question. They are set so that going down is usually survivable and usually
 * expensive, which is what the six-month cap implies the original intended.
 * One rule is not probabilistic at all: a pilot the sim says was **shot in the
 * cockpit or bled out is dead**, with no roll, because the flight model already
 * decided that.
 */

import type { MissionDraw } from "../mission/mission-build.ts";
import { DAYS_PER_MONTH } from "./calendar.ts";

export const CASUALTY_KINDS = ["unhurt", "hospital", "captured", "killed"] as const;

export type CasualtyKind = (typeof CASUALTY_KINDS)[number];

export interface CasualtyReport {
  readonly kind: CasualtyKind;
  /** Days off flying duty. Never more than the manual's six months. */
  readonly lostDays: number;
  readonly text: string;
}

/** The manual's cap: six 28-day months. */
export const MAX_LOST_DAYS = 6 * DAYS_PER_MONTH;

const KILLED_IN_CRASH_CHANCE = 0.2;
const HOSPITAL_AFTER_CRASH_CHANCE = 0.65;

export interface CasualtyInput {
  /** The sim's own verdict: the pilot, not the aeroplane, was killed. */
  readonly pilotKilled: boolean;
  /** The aeroplane was destroyed. */
  readonly aircraftLost: boolean;
  readonly wounded: boolean;
  readonly overFriendlyTerritory: boolean;
  /**
   * The aeroplane was put down intact, wheels-down and stopped, rather than
   * crashed. On the wrong side of the wire that is its own ending: the manual
   * says *"if you **land** or crash (and survive) in enemy territory, you may
   * end up in an enemy prisoner-of-war camp"*, so a tidy forced landing behind
   * German lines ends the same way a crash there does.
   */
  readonly landedSafely?: boolean;
  /**
   * He went over the side and the canopy held (AD-021, a declared port
   * enhancement — `docs/engineering/bail-out-design.md`).
   *
   * Only ever true for a pilot who is **alive on the ground**: a jump that
   * ended badly arrives here as `pilotKilled`, which is decided above any roll.
   */
  readonly baledOut?: boolean;
  readonly draw: MissionDraw;
}

const UNHURT: CasualtyReport = { kind: "unhurt", lostDays: 0, text: "You walked away from it." };

function days(draw: MissionDraw, min: number, max: number): number {
  return Math.min(MAX_LOST_DAYS, draw.int(min, max));
}

function hospital(lostDays: number): CasualtyReport {
  return {
    kind: "hospital",
    lostDays,
    text: `Stretchered off to hospital. ${lostDays} days before you are passed fit to fly.`,
  };
}

function captured(lostDays: number): CasualtyReport {
  return {
    kind: "captured",
    lostDays,
    text: `You came down behind enemy lines and were taken prisoner. ${lostDays} days in the camp before you get back over the wire.`,
  };
}

const KILLED: CasualtyReport = {
  kind: "killed",
  lostDays: 0,
  text: "You did not get out of it.",
};

/** Wounded but flew home: a shorter stay than a crash, and no aeroplane lost. */
function walkingWounded(draw: MissionDraw): CasualtyReport {
  return hospital(days(draw, 14, 70));
}

/**
 * A parachute landing hurts him about as often as a heavy arrival does, and
 * never kills him — AD-021's whole point is that the 20 % `KILLED_IN_CRASH_CHANCE`
 * is a roll for *riding the aeroplane down*, and he did not.
 *
 * **`PARACHUTE_INJURY_CHANCE` is port-side and invented**, exactly like the two
 * crash probabilities above it, and it is set below them on purpose: a canopy
 * arrival is roughly a jump off a wall, where a crash is a crash. The stay it
 * buys is the short one — `walkingWounded`'s band, not the crash band — because
 * a broken ankle is not a month in a ward.
 */
const PARACHUTE_INJURY_CHANCE = 0.25;

function parachuteLanding(input: CasualtyInput): CasualtyReport {
  // The wrong side of the wire ends the same way it does for a forced landing:
  // the manual's *"if you land or crash (and survive) in enemy territory, you
  // may end up in an enemy prisoner-of-war camp"* does not ask how he arrived.
  if (!input.overFriendlyTerritory) {
    return captured(days(input.draw, 3 * DAYS_PER_MONTH, MAX_LOST_DAYS));
  }
  if (input.wounded) return walkingWounded(input.draw);
  return input.draw.float() < PARACHUTE_INJURY_CHANCE ? walkingWounded(input.draw) : UNHURT;
}

export function resolveCasualty(input: CasualtyInput): CasualtyReport {
  if (input.pilotKilled) return KILLED;
  // **The divergence AD-021 exists for.** Above this line a pilot whose
  // aeroplane was destroyed faces `KILLED_IN_CRASH_CHANCE`; below it, one in
  // five careers ends. A pilot who jumped skips that roll entirely, which is
  // the difference between a career that continues and a career that does not.
  // Placed second, under `pilotKilled`, so a failed jump is still fatal.
  if (input.baledOut) return parachuteLanding(input);
  // An intact forced landing, and only that: an aeroplane that was destroyed
  // did not land, whatever else the caller says, so the crash path below owns it.
  if (input.landedSafely && !input.aircraftLost && !input.overFriendlyTerritory) {
    return captured(days(input.draw, 3 * DAYS_PER_MONTH, MAX_LOST_DAYS));
  }
  if (!input.aircraftLost) return input.wounded ? walkingWounded(input.draw) : UNHURT;
  const roll = input.draw.float();
  if (roll < KILLED_IN_CRASH_CHANCE) return KILLED;
  if (!input.overFriendlyTerritory) return captured(days(input.draw, 3 * DAYS_PER_MONTH, MAX_LOST_DAYS));
  if (roll < HOSPITAL_AFTER_CRASH_CHANCE) return hospital(days(input.draw, DAYS_PER_MONTH, 5 * DAYS_PER_MONTH));
  return input.wounded ? walkingWounded(input.draw) : UNHURT;
}
